/* global BigInt */
// `BigInt` is an ES2020 global (both browsers + Node). The project's
// ESLint config pre-dates the 2020 `env` add, so it doesn't know
// about it — the hint above keeps lint happy without a config churn.
import { useCallback, useEffect, useRef, useState } from "react";
import {
  useAccount,
  useChainId,
  useSendTransaction,
  useSignMessage,
  useSwitchNetwork,
} from "wagmi";
import { encodeFunctionData, parseAbi } from "viem";
import { confirmDirectPayment } from "../api/paymentLinksApi";

/**
 * ERC-20 `transfer(address,uint256)` ABI used to encode the
 * calldata for non-native direct transfers. Matches the standard
 * IERC20 selector; parsing the single-row ABI via viem keeps the
 * hook self-contained (no token-specific ABI imports).
 */
const ERC20_TRANSFER_ABI = parseAbi([
  "function transfer(address to, uint256 amount)",
]);

/** Milliseconds between /confirm-direct polls while waiting for
 *  confirmations. Slightly longer than a typical block time so we
 *  don't thrash the backend while still updating the confirmation
 *  counter every few seconds on screens that display it. */
const POLL_INTERVAL_MS = 4000;

/** Overall timeout on the confirmation polling loop — 5 minutes.
 *  Past this we throw so the UI stops the spinner and shows a
 *  "refresh + check on explorer" hint rather than spinning
 *  forever. Backend's `REQUIRED_CONFIRMATIONS` lands well inside
 *  this window on supported chains (Base ~10s, Polygon ~60s). */
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Attestation validity window used on the signing side. The
 * backend rejects attestations whose `expiresAt - now` is more
 * than its own `ATTESTATION_VALIDITY_MS` (30 min). Using 25 min
 * here leaves a 5-min buffer for clock drift + network latency
 * between sign time and the first /confirm-direct POST.
 */
const ATTESTATION_VALIDITY_BUFFER_MS = 25 * 60 * 1000;

/**
 * Build the plain-text attestation the payer signs to prove they
 * sent the tx. MUST match the backend's
 * `buildAttestationMessage` byte-for-byte — any drift breaks
 * `recoverMessageAddress` on the Lambda side. Kept here as a
 * local helper rather than shared with the backend because the
 * two live in separate npm workspaces (webapp vs
 * backend/wallet-management); duplicating the ~12-line string
 * builder is simpler than setting up a shared package.
 */
function buildAttestationMessage({ linkId, txHash, payerAddress, expiresAt }) {
  return [
    "Horus Wallet Payment Confirmation",
    "",
    "I am claiming this on-chain payment for the linked Horus payment link.",
    "",
    `Link ID: ${linkId}`,
    `Transaction: ${txHash}`,
    `Payer: ${payerAddress}`,
    `Expires: ${new Date(expiresAt).toISOString()}`,
    "",
    "Signing this message proves you are the wallet that sent the transaction.",
    "No funds will be moved by this signature.",
  ].join("\n");
}

/**
 * State machine for the direct-transfer payment flow (P3.2 v2,
 * post-CRITICAL-fix two-popup flow).
 *
 *   idle → switching → sending → attesting → confirming → recording → success
 *                                                                     ↘ error
 *
 * Stages:
 *   - `switching` — wagmi `switchNetworkAsync` (no-op on match).
 *   - `sending`   — wallet popup #1: transfer. Payer confirms.
 *   - `attesting` — wallet popup #2: sign attestation proving they
 *     sent the tx. No funds move on this signature. The backend's
 *     /confirm-direct endpoint rejects without this — replaces the
 *     broken amount-salt replay protection that used to overcharge
 *     low-decimal tokens (USDC, WBTC).
 *   - `confirming`— tx in mempool; poll /confirm-direct every 4s
 *     with the signed attestation. Backend returns 202 retry until
 *     REQUIRED_CONFIRMATIONS, then 200.
 *   - `recording` — brief window between 200 and receipt render.
 *   - `success`   — ReceiptCard renders.
 *
 * UX messaging for the two-popup flow:
 *   Before tx  → "Step 1 of 2: Send the payment"
 *   After tx   → "Step 2 of 2: Confirm you sent it (no gas, no funds)"
 * CheckoutPanel reads `phase` and shows the right copy.
 *
 * Error narrowing:
 *   - wagmi "user rejected" → "Signature cancelled in wallet."
 *   - TxAlreadyUsedError    → "This tx was already used."
 *   - POLL_TIMEOUT hit      → "Confirmation timeout — check explorer."
 *   - everything else       → the thrown Error's message.
 */
export function useDirectPay(link) {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { switchNetworkAsync } = useSwitchNetwork();
  const { sendTransactionAsync } = useSendTransaction();
  // CRITICAL-fix: payer signs a plain-text attestation binding
  // the (linkId, txHash, payer, expiresAt) triple to their
  // wallet address. The backend recovers the signer and asserts
  // equality with `tx.from` + `payerAddress` — blocks receipt
  // theft without any on-chain mutation of the amount.
  const { signMessageAsync } = useSignMessage();

  const [phase, setPhase] = useState("idle");
  const [txHash, setTxHash] = useState(null);
  const [confirmations, setConfirmations] = useState(0);
  const [requiredConfirmations, setRequiredConfirmations] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState(null);

  // Guard a single in-flight pay() call. A double-tap on the Pay
  // button shouldn't pop two wallet popups.
  const inFlightRef = useRef(false);

  const reset = useCallback(() => {
    setPhase("idle");
    setTxHash(null);
    setConfirmations(0);
    setRequiredConfirmations(null);
    setReceipt(null);
    setError(null);
    inFlightRef.current = false;
  }, []);

  // Reset on link change so stale state from a previous link
  // doesn't bleed into the new page view.
  useEffect(() => {
    reset();
    // We deliberately don't depend on `reset` (stable useCallback) —
    // reset only when the link itself changes.
  }, [link?.linkId, reset]);

  const pay = useCallback(async () => {
    if (inFlightRef.current) return;
    if (!isConnected || !address) {
      setError(new Error("Connect a wallet to pay."));
      setPhase("error");
      return;
    }
    // /info projection returns the derived `paymentMethods` array
    // but not the raw `supportsDirect` flag — read from the array.
    // In normal flow the CheckoutPanel already gates `activeMethod`
    // on `paymentMethods`, so this is defense-in-depth.
    if (!link?.paymentMethods?.includes("direct")) {
      setError(
        new Error(
          "This link doesn't support direct transfer. Use the gasless wallet flow instead.",
        ),
      );
      setPhase("error");
      return;
    }

    inFlightRef.current = true;
    setError(null);
    setReceipt(null);

    try {
      // 1. Chain switch (no-op when already on the right chain).
      //    wagmi pops up the wallet for the switch; payer declines
      //    surfaces as a thrown Error which we catch below.
      const targetChainId = Number(link.chainId);
      if (!Number.isFinite(targetChainId)) {
        throw new Error("Payment link is missing a chain id — contact the creator.");
      }
      if (chainId !== targetChainId) {
        setPhase("switching");
        if (!switchNetworkAsync) {
          throw new Error(
            `Please switch your wallet to ${link.network} (chain ${targetChainId}) and retry.`,
          );
        }
        await switchNetworkAsync(targetChainId);
      }

      // 2. Build tx params. Native → `value` transfer. ERC-20 →
      //    `transfer(to, amount)` encoded calldata to the token
      //    contract with `value: 0`. `link.amount` is the exact
      //    atomic amount the user requested — no salt, no
      //    mutation (CRITICAL-fix dropped the salt that was
      //    overcharging low-decimal tokens). The payer's wallet
      //    shows the clean value + the page's `amountDisplay`
      //    matches byte-for-byte.
      let txParams;
      if (link.isNative) {
        txParams = {
          to: link.payTo,
          value: BigInt(link.amount),
          chainId: targetChainId,
        };
      } else {
        txParams = {
          to: link.tokenAddress,
          data: encodeFunctionData({
            abi: ERC20_TRANSFER_ABI,
            functionName: "transfer",
            args: [link.payTo, BigInt(link.amount)],
          }),
          chainId: targetChainId,
        };
      }

      // 3. Send tx — wallet popup #1, payer confirms transfer.
      //
      // wagmi v1's `sendTransactionAsync` resolves to
      // `{ hash: '0x…' }` (SendTransactionResult), NOT a bare string.
      // Pull the hash out and guard the shape — the backend's
      // /confirm-direct endpoint rejects non-32-byte hex with
      // "txHash is required".
      setPhase("sending");
      const sendResult = await sendTransactionAsync(txParams);
      const hash = sendResult?.hash || sendResult;
      if (typeof hash !== "string" || !/^0x[a-fA-F0-9]{64}$/.test(hash)) {
        throw new Error("Wallet didn't return a valid transaction hash.");
      }
      setTxHash(hash);

      // 4. CRITICAL fix — sign the attestation that proves this
      //    wallet sent the tx. Wallet popup #2. No gas, no funds
      //    move. The backend's /confirm-direct requires this +
      //    recovers the signer to block receipt theft. `expiresAt`
      //    must fit inside the backend's ATTESTATION_VALIDITY_MS
      //    window (30 min); we use 25 min here for clock-drift
      //    buffer.
      setPhase("attesting");
      const expiresAt = Date.now() + ATTESTATION_VALIDITY_BUFFER_MS;
      const attestationMessage = buildAttestationMessage({
        linkId: link.linkId,
        txHash: hash,
        payerAddress: address,
        expiresAt,
      });
      const signature = await signMessageAsync({ message: attestationMessage });

      // 5. Poll /confirm-direct until it returns 200 or times out.
      //    Backend returns 202 `retry: true` with diagnostic
      //    confirmation counters until the tx lands + meets the
      //    per-chain REQUIRED_CONFIRMATIONS threshold.
      setPhase("confirming");
      const startTime = Date.now();
      // Infinite-loop until success / timeout / non-retry error.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() - startTime > POLL_TIMEOUT_MS) {
          throw new Error(
            "Confirmation timeout. Check the transaction on the block explorer and refresh.",
          );
        }
        try {
          const result = await confirmDirectPayment(
            link,
            hash,
            address,
            signature,
            expiresAt,
          );
          setPhase("recording");
          setReceipt(result.receipt);
          setPhase("success");
          return;
        } catch (err) {
          if (err?.retry) {
            // Backend: not-yet-confirmed. Update counters + retry.
            if (Number.isFinite(err.confirmations)) {
              setConfirmations(Number(err.confirmations));
            }
            if (Number.isFinite(err.required)) {
              setRequiredConfirmations(Number(err.required));
            }
            await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
            continue;
          }
          // Non-retry error — bubble up.
          throw err;
        }
      }
    } catch (err) {
      // Narrow common wallet rejections to friendly copy. All other
      // errors (chain mismatch, backend 4xx, RPC down) fall through
      // to their raw message — backend responses already carry
      // user-safe strings.
      let message = err?.message || "Direct payment failed.";
      const lowered = message.toLowerCase();
      if (lowered.includes("user rejected") || lowered.includes("user denied")) {
        message = "Signature cancelled in wallet.";
      }
      const narrowed = new Error(message);
      narrowed.statusCode = err?.statusCode;
      narrowed.reason = err?.reason;
      setError(narrowed);
      setPhase("error");
    } finally {
      inFlightRef.current = false;
    }
  }, [
    link,
    isConnected,
    address,
    chainId,
    switchNetworkAsync,
    sendTransactionAsync,
    signMessageAsync,
  ]);

  return {
    phase,
    txHash,
    confirmations,
    requiredConfirmations,
    receipt,
    error,
    pay,
    reset,
  };
}
